import React, { useEffect, useMemo, useState } from 'react';
import { HiChevronLeft } from 'react-icons/hi';
import CustomButton from '../../../../components/common/CustomButton';
import UnsavedChangesIndicator from '../../../../components/common/UnsavedChangesIndicator';
import CustomTooltip from '../../../../components/common/CustomTooltip';
import NotificationTypeList from './TriggerWizard/NotificationTypeList';
import ConfigPanel from './TriggerWizard/Config/ConfigPanel';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearSlackWizardEntities,
  resetSlackWizard,
  setSlackWizardSelectedChannels,
  setSlackWizardSelectedEntities,
  setSlackWizardSelectedEntitiesData,
  setSlackWizardSelectedEntityType,
  setSlackWizardSelectedFrequency,
  setSlackWizardSelectedNotificationKey,
  setSlackWizardSelectedTaskTriggerTypes,
  setSlackWizardSelectionMethod,
  setSlackWizardSprintIntegration,
  setSlackWizardStep,
  setSlackWizardCronConfig
} from '../../../../redux/slices/settings';
import { AvailableNotification, ChannelOption, CronConfig, FrequencyType, SlackChannel, SlackUser } from "../../types";
import {
  getAvailableFrequencies,
  getBackendEntityType,
  getEntitySelectionType,
  requiresEntitySelection
} from '../../../../utils/entityTypeUtils';
import useSlackService from '../../../../services/slack';

interface NotificationOption {
  notification: AvailableNotification;
  label: string;
  description: string;
}

interface TriggerWizardProps {
  isOpen: boolean;
  onComplete: (data: TriggerConfig) => void | Promise<void>;
  onCancel: () => void;
  mode?: 'create' | 'edit';
  initialConfig?: TriggerConfig;
}

// Entity data for the configuration payload
export interface EntityConfigItem {
  entity_id: string;
  type: string;  // 'roadmap' or 'project' - maps from backend 'type' field
}

export interface TriggerConfig {
  entityType: string;
  entityIds: string[];
  entitiesData?: EntityConfigItem[];
  frequency: FrequencyType;
  sendToChannels: string[];
  notificationKey: string;
  supportedFrequencies: FrequencyType[];
  taskTriggerTypes?: string[];
  selectionScope?: 'active' | 'selective';
  cron?: CronConfig;
  sprintIntegration?: 'jira' | 'azure';
}

const TriggerWizard: React.FC<TriggerWizardProps> = ({
  isOpen,
  onComplete,
  onCancel,
  mode = 'create',
  initialConfig,
}) => {
  const dispatch = useDispatch();
  const {
    slackWizardStep: step = 'entity_type',
    slackWizardSelectedEntityType: selectedEntityType = null,
    slackWizardSelectedEntities: selectedEntities = [],
    slackWizardSelectedEntitiesData: selectedEntitiesData = [],
    slackWizardSelectedFrequency: selectedFrequency = 'daily',
    slackWizardSelectedChannels: selectedChannels = [],
    slackWizardSelectedTaskTriggerTypes: selectedTaskTriggers = [],
    slackWizardSelectedNotificationKey: selectedNotificationKey = null,
    slackWizardSelectionMethod: selectionMethod = null,
    slackWizardCronConfig: cronConfig = { day: 1, hour: 9, minute: 0, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' },
    slackWizardSprintIntegration: sprintIntegration = null,
    slackAvailableNotifications: availableNotifications = [],
    slackChannels = [],
    slackUsers = [],
    projectIdNameMap = {},
    roadmapIdNameMap = {},
    strategyMapIdNameMap = {}
  } = useSelector((state: any) => state.settings);
  const { selectedOrgId } = useSelector((state: any) => state.organization);
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const { testSlackNotification } = useSlackService();

  const notificationOptions = useMemo<NotificationOption[]>(() => {
    return availableNotifications.map((notification: AvailableNotification) => ({
      notification,
      label: notification.name,
      description: notification.description,
    }));
  }, [availableNotifications]);

  const selectedNotificationOption = useMemo<NotificationOption | null>(() => {
    if (!selectedNotificationKey) return null;
    return (
      notificationOptions.find((option) => option.notification.key === selectedNotificationKey) || null
    );
  }, [notificationOptions, selectedNotificationKey]);

  useEffect(() => {
    if (isOpen && mode === 'create' && !initialConfig) {
      // Single reset to avoid multiple renders
      dispatch(resetSlackWizard());
      setIsLoading(false);
    }
  }, [dispatch, isOpen, mode, initialConfig]);

  useEffect(() => {
    if (!selectedNotificationOption) {
      return;
    }
    const frequencies = getAvailableFrequencies(selectedNotificationOption.notification) as FrequencyType[];
    if (!frequencies.includes(selectedFrequency)) {
      dispatch(setSlackWizardSelectedFrequency(frequencies[0]));
    }
  }, [dispatch, selectedNotificationOption, selectedFrequency]);

  // Seed edit mode from initialConfig
  useEffect(() => {
    // Only seed when entering edit and the wizard is still at entity selection step
    if (!isOpen || mode !== 'edit' || !initialConfig || step !== 'entity_type') {
      return;
    }
    // Resolve channel/user options for the provided sendTo IDs
    const channelOptions: ChannelOption[] = (initialConfig.sendToChannels || []).map((id) => {
      const channel = (slackChannels || []).find((c: SlackChannel) => c.id === id);
      if (channel) {
        return { value: channel.id, label: `#${channel.name}`, type: 'channel' };
      }
      const user = (slackUsers || []).find((u: SlackUser) => u.slack_user_id === id);
      if (user) {
        return { value: user.slack_user_id, label: `@${user.name}`, type: 'user' };
      }
      return { value: id, label: id };
    });

    // Simple guard to avoid redundant updates on common fields
    dispatch(setSlackWizardSelectedNotificationKey(initialConfig.notificationKey));
    dispatch(setSlackWizardSelectedEntityType(initialConfig.entityType));
    dispatch(setSlackWizardSelectedEntities(initialConfig.entityIds || []));
    if (initialConfig.entitiesData && initialConfig.entitiesData.length > 0) {
      dispatch(setSlackWizardSelectedEntitiesData(initialConfig.entitiesData as any));
    }
    dispatch(setSlackWizardSelectedFrequency(initialConfig.frequency));
    dispatch(setSlackWizardSelectedTaskTriggerTypes((initialConfig.taskTriggerTypes || []) as any));
    dispatch(setSlackWizardSelectedChannels(channelOptions));
    // Seed cron config if available
    if (initialConfig.cron) {
      dispatch(setSlackWizardCronConfig(initialConfig.cron));
    }
    // Seed sprint integration when editing sprint trigger
    if (initialConfig.sprintIntegration) {
      dispatch(setSlackWizardSprintIntegration(initialConfig.sprintIntegration));
    }
    // Seed selection method for entity-scoped notifications
    const type = getEntitySelectionType(initialConfig.entityType);
    if (initialConfig.entityType === 'work_item' || type === 'strategy_map') {
      const method = initialConfig.selectionScope === 'active' ? 'active' : 'selective';
      dispatch(setSlackWizardSelectionMethod(method));
    }
    dispatch(setSlackWizardStep('config'));
  }, [isOpen, mode, initialConfig, slackChannels, slackUsers, step]);

  const handleNotificationSelectDirect = (notification: AvailableNotification) => {
    handleNotificationSelect({
      notification,
      label: notification.name,
      description: notification.description
    });
  };

  const handleNotificationSelect = (option: NotificationOption) => {
    const entityType = getBackendEntityType(option.notification);
    const frequencies = getAvailableFrequencies(option.notification);
    const defaultFreq = (frequencies[0] || 'daily') as FrequencyType;

    dispatch(setSlackWizardSelectedEntityType(entityType));
    dispatch(setSlackWizardSelectedNotificationKey(option.notification.key));
    dispatch(setSlackWizardSelectedFrequency(defaultFreq));
    dispatch(clearSlackWizardEntities());
    dispatch(setSlackWizardSelectedTaskTriggerTypes([]));
    dispatch(setSlackWizardSelectedChannels([]));
    dispatch(setSlackWizardStep('config'));
  };

  const handleBack = () => {
    if (mode === 'edit') {
      return;
    }
    if (step === 'config') {
      dispatch(setSlackWizardStep('entity_type'));
    } else {
      onCancel();
    }
  };

  const handleComplete = async () => {
    if (!selectedEntityType || !selectedNotificationKey) return;
    const notificationDef = availableNotifications.find((n: AvailableNotification) => n.key === selectedNotificationKey);
    if (!notificationDef) return;

    const selectionType = getEntitySelectionType(selectedEntityType);

    // Build entities data array from selected entities (only entity_id and entity_type for work_item; full sprint shape for sprint)
    const entitiesData: EntityConfigItem[] = (selectedEntityType === 'work_item')
      ? (selectedEntitiesData || []).map((item: any) => ({ entity_id: item.entity_id, type: item.type }))
      : (selectedEntitiesData || []).map((item: any) => ({ ...item }));

    const config: TriggerConfig = {
      entityType: selectedEntityType,
      entityIds: selectedEntities,
      entitiesData: (selectedEntityType === 'work_item' || selectionType === 'sprint') ? entitiesData : undefined,
      frequency: selectedFrequency,
      sendToChannels: (selectedChannels || []).map((option: ChannelOption) => option.value),
      notificationKey: selectedNotificationKey,
      supportedFrequencies: getAvailableFrequencies(notificationDef) as FrequencyType[],
    };

    if (selectionType === 'task') {
      config.taskTriggerTypes = selectedTaskTriggers;
    } else if (selectedEntityType === 'work_item' || selectionType === 'strategy_map') {
      config.selectionScope = selectionMethod === 'active' ? 'active' : 'selective';
    } else if (selectionType === 'sprint') {
      config.sprintIntegration = sprintIntegration ?? ((selectedEntitiesData?.[0] as any)?.team_id != null ? 'azure' : 'jira');
    }

    // Add cron config for scheduled notifications (non-immediate)
    if (selectedFrequency !== 'immediate' && selectionType !== 'task') {
      config.cron = cronConfig;
    }

    setIsLoading(true);
    try {
      const result = onComplete(config);
      if (result && typeof result === 'object' && 'then' in result) {
        await result;
      }
    } catch (error) {
      console.error('Error completing trigger:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestNotification = async () => {
    if (!selectedEntityType || !selectedChannels || selectedChannels.length === 0) {
      return;
    }

    // Build send_to array from selected channels
    const sendTo = selectedChannels.map((option: ChannelOption) => ({
      slack_id: option.value,
      target_type: option.type || 'channel'
    }));

    setIsTesting(true);
    try {
      await testSlackNotification(selectedEntityType, sendTo);
    } catch (error) {
      console.error('Error testing notification:', error);
    } finally {
      setIsTesting(false);
    }
  };

  const canProceed = () => {
    if (!selectedNotificationKey) {
      return false;
    }
    if (step === 'config') {
      const selectionType = selectedEntityType ? getEntitySelectionType(selectedEntityType) : null;
      if (selectionType === 'task') {
        // Task notifications go to assigned users, no recipients required
        return (selectedTaskTriggers?.length || 0) > 0;
      }
      if (selectedEntityType && requiresEntitySelection(selectedEntityType)) {
        // Allow proceed if active scope selected even with 0 selected entities
        const hasValidScope = selectionMethod === 'active' || (selectedEntities?.length || 0) > 0;
        return hasValidScope && (selectedChannels?.length || 0) > 0;
      }
      return (selectedChannels?.length || 0) > 0;
    }
    return true;
  };

  const getDisabledReason = (): string => {
    if (!selectedNotificationKey) {
      return 'Please select a notification type to continue';
    }
    if (step === 'config') {
      const selectionType = selectedEntityType ? getEntitySelectionType(selectedEntityType) : null;
      const missingFields: string[] = [];

      if (selectionType === 'task') {
        // Task notifications go to assigned users, only trigger events required
        if ((selectedTaskTriggers?.length || 0) === 0) {
          missingFields.push('Trigger Events');
        }
      } else if (selectedEntityType && requiresEntitySelection(selectedEntityType)) {
        if (selectionType === 'sprint') {
          if ((selectedEntities?.length || 0) === 0) {
            missingFields.push('Teams');
          }
        } else if (selectionMethod === 'selective' && (selectedEntities?.length || 0) === 0) {
          if (selectedEntityType === 'work_item') {
            missingFields.push('Work Units');
          } else if (selectionType === 'strategy_map') {
            missingFields.push('Strategy Maps');
          }
        }
        if ((selectedChannels?.length || 0) === 0) {
          missingFields.push('Recipients');
        }
      } else {
        if ((selectedChannels?.length || 0) === 0) {
          missingFields.push('Recipients');
        }
      }

      if (missingFields.length > 0) {
        return `Please fill in the required fields: ${missingFields.join(', ')}`;
      }
    }
    return '';
  };

  const hasUnsavedChanges = useMemo(() => {
    if (mode !== 'edit' || !initialConfig || step !== 'config') return false;
    // @ts-ignore
    const eqSet = (a: Set<any>, b: Set<any>) => a.size === b.size && [...a].every(x => b.has(x));
    if (selectedNotificationKey !== initialConfig.notificationKey) return true;
    if (selectedEntityType !== initialConfig.entityType) return true;
    const type = selectedEntityType ? getEntitySelectionType(selectedEntityType) : null;
    // Helper: filter any id list down to what is visible in the UI for the current type
    const buildVisibleSet = (): Set<string> | null => {
      if (selectedEntityType === 'work_item') {
        return new Set<string>(Object.keys(projectIdNameMap || {}));
      }
      if (type === 'strategy_map') {
        return new Set<string>(Object.keys(strategyMapIdNameMap || {}));
      }
      return null;
    };
    const filterToVisible = (ids: string[] | undefined | null, visible: Set<string> | null) => {
      const list = (ids || []).map((x) => String(x));
      if (!visible) return list;
      return list.filter((id) => visible.has(id));
    };
    if (selectedEntityType === 'work_item' || type === 'strategy_map') {
      const curScope = selectionMethod === 'active' ? 'active' : 'selective';
      const initScope = initialConfig.selectionScope === 'active' ? 'active' : 'selective';
      if (curScope !== initScope) return true;
      if (curScope === 'selective' && initScope === 'selective') {
        const visible = buildVisibleSet();
        const filteredSelected = filterToVisible(selectedEntities, visible);
        const filteredInitial = filterToVisible(initialConfig.entityIds, visible);
        if (!eqSet(new Set(filteredSelected), new Set(filteredInitial))) return true;
      }
    } else {
      if (!eqSet(new Set(selectedEntities || []), new Set(initialConfig.entityIds || []))) return true;
    }
    if (selectedFrequency !== initialConfig.frequency) return true;
    if (!eqSet(new Set((selectedChannels || []).map((o: ChannelOption) => o.value)), new Set(initialConfig.sendToChannels || []))) return true;
    if (!eqSet(new Set(selectedTaskTriggers || []), new Set(initialConfig.taskTriggerTypes || []))) return true;

    // Check cron config changes for non-immediate frequencies
    if (selectedFrequency !== 'immediate' && type !== 'task') {
      const initialCron = initialConfig.cron;
      if (initialCron) {
        if (cronConfig.day !== initialCron.day ||
          cronConfig.hour !== initialCron.hour ||
          cronConfig.minute !== initialCron.minute ||
          cronConfig.timezone !== initialCron.timezone) {
          return true;
        }
      }
    }

    return false;
  }, [
    mode,
    initialConfig,
    step,
    selectedNotificationKey,
    selectedEntityType,
    selectedEntities,
    selectedFrequency,
    selectedChannels,
    selectedTaskTriggers,
    selectionMethod,
    projectIdNameMap,
    strategyMapIdNameMap,
    cronConfig,
  ]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <button
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
        onClick={onCancel}
      />

      {/* Side Panel */}
      <div className="fixed inset-y-0 right-0 w-1/2 bg-tertiary-light dark:bg-tertiary-dark shadow-xl z-50 flex flex-col transform transition-transform duration-300 ease-in-out overflow-hidden">
        {/* Header */}
        <div className="flex flex-col border-b bg-light-surfacePrimary border-gray-500 dark:border-gray-700 !p-4 !pl-6 !pt-5 dark:bg-tertiary-dark sticky top-0 z-40 shadow-lg rounded-b-2xl">
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-4">
              {/* {mode !== 'edit' && step !== 'entity_type' && (
                <button
                  onClick={handleBack}
                  className="p-2 hover:bg-light-interactive-hover dark:hover:bg-hover-dark rounded-lg transition-all duration-200 group"
                  title="Back"
                >
                  <svg className="w-5 h-5 text-light-text-secondary dark:text-gray-400 group-hover:text-light-text-primary dark:group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )} */}

              <div className="flex flex-col min-w-0 max-w-full">
                <div className="text-light-text-primary dark:text-white text-lg font-semibold font-['Inter'] leading-tight truncate pr-2">
                  {mode === 'edit' ? 'Edit Notification Trigger' : 'Create Notification Trigger'}
                </div>
                <div className="mt-1 flex flex-col gap-1">
                  {step === 'config' && selectedNotificationOption && (
                    <div className="text-light-text-tertiary dark:text-white/60 text-sm">
                      {selectedNotificationOption.label}
                    </div>
                  )}

                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="text-light-text-secondary hover:text-light-text-primary dark:text_WHITE/70 dark:hover:text-white hover:bg-light-interactive-hover dark:hover:bg-white/10 p-2 rounded-lg transition-colors duration-200"
                onClick={onCancel}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  ></path>
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-8 flex-1 overflow-y-auto bg-tertiary-light dark:bg-tertiary-dark">
          {step === 'entity_type' && (
            <NotificationTypeList
              selectedNotificationKey={selectedNotificationKey}
              onSelect={handleNotificationSelectDirect}
            />
          )}

          {step === 'config' && (
            <ConfigPanel />
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 items-center justify-between flex gap-4 border-t rounded-t-md border-gray-500 dark:border-gray-700 bg-light-surfacePrimary dark:bg-tertiary-dark shadow-lg">
          {step === 'config' ? (
            <>
              <div className="flex gap-3">
                {mode === 'edit' ? (
                  <CustomButton variant="cancel" onClick={onCancel} className="ml-2">
                    Cancel
                  </CustomButton>
                ) : (
                  <CustomButton variant="cancel" onClick={handleBack} className="ml-2" leftIcon={<HiChevronLeft className="w-4 h-4 -ml-3" />}>
                    Back
                  </CustomButton>
                )}
              </div>
              <div className="flex gap-3 items-center">
                {/* Hide Send Test for task type - no recipients to test with */}
                {mode !== 'edit' && getEntitySelectionType(selectedEntityType) !== 'task' && (
                  <CustomTooltip
                    title={!selectedChannels || selectedChannels.length === 0 ? 'Select recipients first to send a test notification' : 'Send a test notification to verify your setup'}
                    placement="top"
                  >
                    <div
                      className={!selectedChannels || selectedChannels.length === 0 ? 'inline-block cursor-not-allowed' : 'inline-block'}
                      style={{ pointerEvents: 'auto' }}
                    >
                      <CustomButton
                        variant="secondary"
                        onClick={handleTestNotification}
                        disabled={!selectedChannels || selectedChannels.length === 0}
                        isLoading={isTesting}
                      >
                        {isTesting ? 'Sending...' : 'Send Test'}
                      </CustomButton>
                    </div>
                  </CustomTooltip>
                )}
                {mode === 'edit' && (
                  <UnsavedChangesIndicator hasUnsavedChanges={hasUnsavedChanges} className="mr-2" />
                )}
                <CustomTooltip
                  title={!canProceed() ? getDisabledReason() : ''}
                  placement="top"
                >
                  <div
                    className={!canProceed() ? 'inline-block cursor-not-allowed' : 'inline-block'}
                    style={{ pointerEvents: 'auto' }}
                  >
                    <CustomButton
                      onClick={handleComplete}
                      disabled={!canProceed()}
                      isLoading={isLoading}
                    >
                      {mode === 'edit' ? 'Save Changes' : 'Add Trigger'}
                    </CustomButton>
                  </div>
                </CustomTooltip>
              </div>
            </>
          ) : (
            <>
              <div className="flex gap-3">
                <CustomButton variant="cancel" onClick={onCancel} className="ml-2">
                  Cancel
                </CustomButton>
              </div>
              <div className="flex gap-3" />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default TriggerWizard;